import * as Sentry from '@sentry/react';
import type { PeerMetadata } from '@playcastdotio/Messaging';
import type {
  IPeerConnection,
  IPeerConnectionFactory,
  PeerConnectionOptions,
  WebRtcOptimizationConfig,
} from '@playcastdotio/PeerCore';
import {
  SdpInspector,
  WebRtcOptimizationManager,
  configFromMetadata,
  IceHealthGuard,
} from '@playcastdotio/PeerCore';
import type { PeerType } from '../types';

const DIAG = '[AUDIO-BUG-12009:PeerConnection]';

export type PeerConnectionInfo = {
  peerId: string;
  peerType: PeerType;
  metadata: PeerMetadata;
  connection: IPeerConnection;
  optimizationConfig: WebRtcOptimizationConfig;
};

export type PeerConnectionManagerCallbacks = {
  onPeerConnected: (peerId: string, peerType: PeerType, metadata: PeerMetadata) => void;
  onPeerDisconnected: (peerId: string, peerType: PeerType) => void;
  onSignalGenerated: (peerId: string, description: RTCSessionDescriptionInit) => void;
  onIceCandidatesGenerated: (peerId: string, candidates: RTCIceCandidate[]) => void;
  onRemoteStream: (peerId: string, stream: MediaStream) => void;
  onDataChannelMessage: (peerId: string, data: string | ArrayBuffer) => void;
};

/**
 * PeerConnectionManager - Manages WebRTC peer connections via @bowbee/peer-lite.
 *
 * Uses injectable IPeerConnectionFactory for testability.
 * Host-side: manages Map<peerId, IPeerConnection>, each remote peer gets polite=true.
 * Client-side: single connection with polite=false.
 */
export class PeerConnectionManager {
  private connections = new Map<string, PeerConnectionInfo>();
  private factory: IPeerConnectionFactory;
  private callbacks: PeerConnectionManagerCallbacks;
  private defaultIceConfig: RTCConfiguration = {};
  private optimizationManager = new WebRtcOptimizationManager();
  private sdpInspector = new SdpInspector();
  private bitrateOverride: number | null = null;
  private preWarmedConnection: IPeerConnection | null = null;
  private preWarmCreatedAt = 0;
  private static readonly PREWARM_MAX_AGE_MS = 60_000;
  /**
   * Holds ICE candidates that arrive before setRemoteDescription has resolved.
   * Drained via drainPendingIceCandidates() after signal(sdp) completes.
   * Prevents the InvalidStateError (HOST-FRONTEND-Z) caused by addIceCandidate
   * racing against setRemoteDescription in the concurrent signaling handler.
   */
  private pendingIceCandidates = new Map<string, RTCIceCandidate[]>();

  constructor(factory: IPeerConnectionFactory, callbacks: PeerConnectionManagerCallbacks) {
    this.factory = factory;
    this.callbacks = callbacks;
  }

  public setIceConfig(config: RTCConfiguration): void {
    this.defaultIceConfig = config;
  }

  /**
   * Override the max video bitrate (in bps) for all current and future peers.
   * Pass null to revert to the per-peer defaults from configFromMetadata().
   */
  public setBitrateOverride(bitrateBps: number | null): void {
    this.bitrateOverride = bitrateBps;
    const label = bitrateBps !== null ? `${(bitrateBps / 1_000_000).toFixed(1)} Mbps` : 'default';
    console.log(`[PeerConnectionManager] Bitrate override set to ${label}`);

    for (const info of this.connections.values()) {
      const rtcPc = info.connection.getRtcPeerConnection();
      if (!rtcPc) continue;

      const effectiveConfig: WebRtcOptimizationConfig = bitrateBps !== null
        ? { ...info.optimizationConfig, maxBitrate: bitrateBps }
        : info.optimizationConfig;

      this.optimizationManager.applySenderParameters(rtcPc, effectiveConfig);
    }
  }

  public getBitrateOverride(): number | null {
    return this.bitrateOverride;
  }

  /**
   * Pre-warm an RTCPeerConnection with ICE candidate gathering only.
   * No tracks, no transceivers, no signaling, no server-side state.
   * Purely local STUN/TURN binding.
   *
   * Do NOT pre-declare transceivers here. The host is the answerer — the
   * incoming client offer already contains m=audio/m=video, and addTrack()
   * (called in createHostSideConnection() before signal()) will create
   * sendonly transceivers with the actual tracks attached. Pre-declaring
   * empty sendonly transceivers caused JSEP to match them (instead of the
   * track-bearing ones) to the offer's m-sections, producing an
   * `a=inactive` answer and the consistent "blue screen" regression.
   */
  public preWarm(): void {
    this.destroyPreWarmedConnection();
    const options: PeerConnectionOptions = {
      id: 'pre-warm',
      config: {
        ...this.defaultIceConfig,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceCandidatePoolSize: 10,
      },
      batchCandidates: true,
      batchCandidatesTimeout: 1000,
      enableDataChannels: true,
      channelLabel: 'data',
    };
    this.preWarmedConnection = this.factory.create(options);
    this.preWarmCreatedAt = Date.now();
    console.log('[PeerConnectionManager] Pre-warmed RTCPeerConnection with iceCandidatePoolSize=10');
  }

  public hasPreWarmedConnection(): boolean {
    return this.preWarmedConnection !== null;
  }

  public destroyPreWarmedConnection(): void {
    if (this.preWarmedConnection) {
      this.preWarmedConnection.destroy();
      this.preWarmedConnection = null;
    }
  }

  /**
   * Create a new peer connection for a remote peer (host-side, polite=true).
   *
   * Order matters: start() must be called before addTrack/codec prefs because
   * peer-lite creates the RTCPeerConnection during start(). Events are wired
   * first so we don't miss anything.
   */
  public createHostSideConnection(
    peerId: string,
    peerType: PeerType,
    metadata: PeerMetadata,
    videoTrack: MediaStreamTrack | null,
    audioTrack: MediaStreamTrack | null,
  ): IPeerConnection {
    const existing = this.connections.get(peerId);
    if (existing) {
      console.log(`[PeerConnectionManager] Destroying stale connection for reconnecting peer: ${peerId}`);
      existing.connection.destroy();
      this.connections.delete(peerId);
    }

    const baseConfig = configFromMetadata(metadata);
    const optimizationConfig = this.bitrateOverride !== null
      ? { ...baseConfig, maxBitrate: this.bitrateOverride }
      : baseConfig;

    let connection: IPeerConnection;

    if (this.preWarmedConnection && this.isPreWarmHealthy()) {
      console.log('[PeerConnectionManager] Using pre-warmed connection for peer:', peerId);
      connection = this.preWarmedConnection;
      this.preWarmedConnection = null;
    } else {
      if (this.preWarmedConnection) {
        console.warn('[PeerConnectionManager] Discarding stale/unhealthy pre-warmed connection');
        this.destroyPreWarmedConnection();
      }
      const options = this.buildConnectionOptions(peerId);
      connection = this.factory.create(options);
    }

    this.wireConnectionEvents(peerId, peerType, metadata, connection);
    this.connections.set(peerId, { peerId, peerType, metadata, connection, optimizationConfig });

    // The host is the answerer. The client's offer already declares
    // m=audio/m=video, so addTrack() below (BEFORE signal()) creates sendonly
    // transceivers with real tracks + stream associations that JSEP will
    // match cleanly to mid:0/mid:1. Pre-creating empty sendonly transceivers
    // here caused Chrome to match the empty ones first, producing an
    // `a=inactive` answer (the "blue screen" regression).
    connection.start({ polite: true });

    if (videoTrack) {
      connection.addTrack(videoTrack);
    } else {
      console.warn(`${DIAG} No video track to attach for peer ${peerId} (type=${peerType})`);
      Sentry.addBreadcrumb({
        category: 'BLUE-STREAM-12010',
        message: 'No video track available for peer connection',
        level: 'warning',
        data: { peerId, peerType },
      });
    }
    if (audioTrack) {
      console.log(`${DIAG} Adding audio track to peer ${peerId}`, {
        label: audioTrack.label, readyState: audioTrack.readyState, enabled: audioTrack.enabled, muted: audioTrack.muted,
      });
      connection.addTrack(audioTrack);
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: `Audio track added to peer connection`,
        level: 'info',
        data: { peerId, peerType, label: audioTrack.label, readyState: audioTrack.readyState, enabled: audioTrack.enabled },
      });
    } else {
      console.warn(`${DIAG} No audio track to attach for peer ${peerId} (type=${peerType})`);
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: 'No audio track available for peer connection',
        level: 'warning',
        data: { peerId, peerType },
      });
    }

    this.optimizationManager.applyPreConnectionOptimizations(
      connection.getRtcPeerConnection(),
      optimizationConfig,
    );

    return connection;
  }

  /**
   * Create a client-side connection (impolite=false, single connection).
   */
  public createClientSideConnection(
    peerId: string,
    peerType: PeerType,
    metadata: PeerMetadata,
  ): IPeerConnection {
    const options = this.buildConnectionOptions(peerId);
    const connection = this.factory.create(options);
    const baseConfig = configFromMetadata(metadata);
    const optimizationConfig = this.bitrateOverride !== null
      ? { ...baseConfig, maxBitrate: this.bitrateOverride }
      : baseConfig;

    this.wireConnectionEvents(peerId, peerType, metadata, connection);
    this.connections.set(peerId, { peerId, peerType, metadata, connection, optimizationConfig });

    connection.start({ polite: false });

    this.optimizationManager.applyPreConnectionOptimizations(
      connection.getRtcPeerConnection(),
      optimizationConfig,
    );

    return connection;
  }

  public getConnection(peerId: string): IPeerConnection | undefined {
    return this.connections.get(peerId)?.connection;
  }

  public getConnectionInfo(peerId: string): PeerConnectionInfo | undefined {
    return this.connections.get(peerId);
  }

  public getAllConnections(): PeerConnectionInfo[] {
    return Array.from(this.connections.values());
  }

  public getConnectionCount(): number {
    return this.connections.size;
  }

  public async handleRemoteSdp(peerId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    const conn = this.connections.get(peerId);
    if (!conn) {
      console.warn(`[PeerConnectionManager] No connection found for peer ${peerId}`);
      return;
    }
    await conn.connection.signal(sdp);
    this.logRemoteCandidateCount(peerId, conn.connection);
  }

  private logRemoteCandidateCount(peerId: string, connection: IPeerConnection): void {
    IceHealthGuard.getCandidateCounts(connection).then((counts) => {
      if (!counts) return;
      console.log(`[PeerConnectionManager] Post-SDP stats for ${peerId}: ${counts.remoteCandidates} remote candidates, ${counts.candidatePairs} candidate pairs`);
      Sentry.addBreadcrumb({
        category: 'webrtc',
        message: `Post-setRemoteDescription candidate stats (${peerId})`,
        level: counts.remoteCandidates === 0 ? 'warning' : 'info',
        data: { peerId, ...counts },
      });
    });
  }

  public async handleRemoteIceCandidate(peerId: string, candidate: RTCIceCandidate): Promise<void> {
    const conn = this.connections.get(peerId);
    const rtcPc = conn?.connection.getRtcPeerConnection();

    if (!conn || !rtcPc?.remoteDescription) {
      const pending = this.pendingIceCandidates.get(peerId) ?? [];
      pending.push(candidate);
      this.pendingIceCandidates.set(peerId, pending);
      return;
    }

    await conn.connection.addIceCandidate(candidate);
  }

  /**
   * Apply all ICE candidates that buffered before setRemoteDescription resolved.
   * Must be called after signal(sdp) completes in acceptIncomingPeer so that
   * the remote description is guaranteed to be set.
   */
  public async drainPendingIceCandidates(peerId: string): Promise<void> {
    const pending = this.pendingIceCandidates.get(peerId);
    if (!pending?.length) return;

    this.pendingIceCandidates.delete(peerId);

    const conn = this.connections.get(peerId);
    if (!conn) return;

    console.log(`[PeerConnectionManager] Draining ${pending.length} buffered ICE candidate(s) for peer ${peerId}`);
    for (const candidate of pending) {
      await conn.connection.addIceCandidate(candidate);
    }
  }

  public sendData(peerId: string, data: string | ArrayBuffer, label?: string): boolean {
    const conn = this.connections.get(peerId);
    if (!conn) {
      console.warn(`[PeerConnectionManager] sendData failed: no connection for ${peerId}`);
      return false;
    }
    const result = conn.connection.send(data, label);
    if (!result) {
      console.warn(`[PeerConnectionManager] sendData failed: send() returned false for ${peerId}`);
    }
    return result;
  }

  public broadcastData(data: string | ArrayBuffer, label?: string): void {
    for (const info of this.connections.values()) {
      if (info.connection.isConnected()) {
        info.connection.send(data, label);
      }
    }
  }

  /**
   * Request a single keyframe from the video encoder for a specific peer.
   * Uses the Chromium-specific second argument to setParameters(); safe on the
   * host (Electron) and a silent no-op if the API is unavailable.
   */
  public requestKeyframeForPeer(peerId: string): void {
    const info = this.connections.get(peerId);
    if (!info) return;

    const rtcPc = info.connection.getRtcPeerConnection();
    if (!rtcPc) return;

    for (const sender of rtcPc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      try {
        const params = sender.getParameters();
        (sender as any).setParameters(params, {
          encodingOptions: [{ keyFrame: true }],
        });
        console.log(`[PeerConnectionManager] Keyframe requested for peer ${peerId}`);
      } catch (err) {
        console.warn(`[PeerConnectionManager] Keyframe request failed for ${peerId}:`, err);
      }
      break;
    }
  }

  /**
   * Replace a track on all active connections (e.g. when MediaCoordinator emits tracksChanged).
   */
  public async replaceTrackOnAll(
    oldTrack: MediaStreamTrack,
    newTrack: MediaStreamTrack,
  ): Promise<void> {
    const promises: Promise<void>[] = [];
    for (const info of this.connections.values()) {
      promises.push(info.connection.replaceTrack(oldTrack, newTrack));
    }
    await Promise.allSettled(promises);
  }

  public requestKeyframeForAllPeers(): void {
    for (const peerId of this.connections.keys()) {
      this.requestKeyframeForPeer(peerId);
    }
  }

  /**
   * Attempt an ICE restart for a specific peer. The new offer will flow
   * through the normal 'signal' event → PeerCoordinator → signaling service.
   * Returns false if no connection exists.
   */
  public async tryIceRestart(peerId: string): Promise<boolean> {
    const conn = this.connections.get(peerId);
    if (!conn) return false;
    console.log(`[PeerConnectionManager] Attempting ICE restart for peer: ${peerId}`);
    try {
      await conn.connection.restartIce();
      return true;
    } catch (err) {
      console.error(`[PeerConnectionManager] ICE restart failed for ${peerId}:`, err);
      return false;
    }
  }

  public destroyConnection(peerId: string): void {
    const conn = this.connections.get(peerId);
    if (conn) {
      conn.connection.destroy();
      this.connections.delete(peerId);
    }
    this.pendingIceCandidates.delete(peerId);
  }

  public destroyAll(): void {
    this.destroyPreWarmedConnection();
    for (const info of this.connections.values()) {
      info.connection.destroy();
    }
    this.connections.clear();
    this.pendingIceCandidates.clear();
  }

  private recordSdpBreadcrumb(
    peerId: string,
    peerType: PeerType,
    description: RTCSessionDescriptionInit,
  ): void {
    const kinds = this.sdpInspector.getMediaKinds(description.sdp);
    const hasVideo = kinds.includes('video');
    Sentry.addBreadcrumb({
      category: 'webrtc',
      message: `Host local SDP (${description.type}) m-sections`,
      level: hasVideo ? 'info' : 'warning',
      data: { peerId, peerType, type: description.type, kinds },
    });
    if (!hasVideo) {
      console.warn(
        `[PeerConnectionManager] Local SDP (${description.type}) for ${peerId} has no m=video — expected sendonly video transceiver.`,
        kinds,
      );
    }
  }

  private isPreWarmHealthy(): boolean {
    if (!this.preWarmedConnection) return false;
    const result = IceHealthGuard.checkPreWarm(
      this.preWarmedConnection, this.preWarmCreatedAt, PeerConnectionManager.PREWARM_MAX_AGE_MS,
    );
    if (!result.healthy) {
      console.warn(`[PeerConnectionManager] Pre-warm unhealthy: ${result.reason}`);
    }
    return result.healthy;
  }

  private buildConnectionOptions(
    peerId: string,
  ): PeerConnectionOptions {
    return {
      id: peerId,
      config: {
        ...this.defaultIceConfig,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      },
      batchCandidates: true,
      batchCandidatesTimeout: 1000,
      enableDataChannels: true,
      channelLabel: 'data',
    };
  }

  private wireConnectionEvents(
    peerId: string,
    peerType: PeerType,
    metadata: PeerMetadata,
    connection: IPeerConnection,
  ): void {
    let iceConnected = false;
    let channelReady = false;
    let notified = false;
    const connStartTime = Date.now();

    const tryNotifyConnected = () => {
      if (notified || !iceConnected || !channelReady) return;
      notified = true;
      console.log(`[PeerConnectionManager] Peer fully ready: ${peerId} (${peerType}) +${Date.now() - connStartTime}ms`);
      this.callbacks.onPeerConnected(peerId, peerType, metadata);
    };

    connection.on('connected', () => {
      console.log(`[PeerConnectionManager] ICE connected: ${peerId} (${peerType}) +${Date.now() - connStartTime}ms`);
      const info = this.connections.get(peerId);
      if (info) {
        const effectiveConfig = this.bitrateOverride !== null
          ? { ...info.optimizationConfig, maxBitrate: this.bitrateOverride }
          : info.optimizationConfig;
        this.optimizationManager.applyPostConnectionOptimizations(
          connection.getRtcPeerConnection(),
          effectiveConfig,
        );
      }
      iceConnected = true;
      tryNotifyConnected();
    });

    connection.on('channelOpen', () => {
      console.log(`[PeerConnectionManager] Data channel open: ${peerId} (${peerType}) +${Date.now() - connStartTime}ms`);
      channelReady = true;
      tryNotifyConnected();
    });

    connection.on('disconnected', () => {
      console.log(`[PeerConnectionManager] Peer disconnected: ${peerId} (${peerType})`);
      this.connections.delete(peerId);
      this.callbacks.onPeerDisconnected(peerId, peerType);
    });

    connection.on('signal', (...args: unknown[]) => {
      const description = args[0] as RTCSessionDescriptionInit;
      this.recordSdpBreadcrumb(peerId, peerType, description);
      this.callbacks.onSignalGenerated(peerId, description);
    });

    connection.on('onicecandidates', (...args: unknown[]) => {
      const candidates = args[0] as RTCIceCandidate[];
      this.callbacks.onIceCandidatesGenerated(peerId, candidates);
    });

    connection.on('streamRemote', (...args: unknown[]) => {
      const stream = args[0] as MediaStream;
      this.callbacks.onRemoteStream(peerId, stream);
    });

    connection.on('channelData', (...args: unknown[]) => {
      const event = args[0] as { data: string | ArrayBuffer };
      this.callbacks.onDataChannelMessage(peerId, event.data);
    });

    connection.on('error', (...args: unknown[]) => {
      const error = args[0] as { id: string; message: string };
      console.error(`[PeerConnectionManager] Peer error ${peerId}:`, error.message);
    });

    this.wireIceRestartHandler(connection, peerId, peerType);
  }

  private wireIceRestartHandler(connection: IPeerConnection, peerId: string, peerType: PeerType): void {
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) return;
    let iceEverChecking = false;
    let restarted = false;
    rtcPc.addEventListener('iceconnectionstatechange', () => {
      const state = rtcPc.iceConnectionState;
      if (state === 'checking') iceEverChecking = true;
      if (state === 'connected' || state === 'completed') restarted = false;
      if (state !== 'failed') return;

      const decision = IceHealthGuard.decideIceFailureAction({ iceEverChecking, alreadyRestarted: restarted });
      if (decision.action === 'restart') {
        restarted = true;
        console.warn(`[PeerConnectionManager] ICE failed for ${peerId} (${peerType}) — attempting restart`);
        Sentry.addBreadcrumb({ category: 'webrtc', message: `Host-side ICE restart for ${peerId}`, level: 'warning', data: { peerId, peerType } });
        void this.tryIceRestart(peerId).then((ok) => {
          if (!ok) this.teardownFailedPeer(peerId, peerType);
        });
      } else {
        console.warn(`[PeerConnectionManager] ICE unrecoverable for ${peerId} (${peerType}): ${decision.action}`);
        this.teardownFailedPeer(peerId, peerType);
      }
    });
  }

  private teardownFailedPeer(peerId: string, peerType: PeerType): void {
    this.destroyConnection(peerId);
    this.callbacks.onPeerDisconnected(peerId, peerType);
  }
}
